import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { downloadCsv } from '~/utils/csv';

import type { ExportTemplateColumn } from '../types/export.types';
import { buildColumnManifest } from '../utils/export-column-selection';

interface ExportColumnManifestButtonProps {
  columns: ExportTemplateColumn[];
  selected: readonly string[];
  /** The active template's encoding delta, so the manifest names each column's rule. */
  transformations: Record<string, string>;
  fileLabel: string;
  className?: string;
  /** Overrides the default "Column list" text; the icon then trails it (a setup-row value). */
  label?: string;
}

/**
 * Downloads the selected columns as a CSV manifest — wire name, label, source
 * column, encoding — for handing to a client or a feed vendor. Shared by the
 * column drawer and the full-page toolbar so both produce the same file.
 */
export function ExportColumnManifestButton({
  columns,
  selected,
  transformations,
  fileLabel,
  className,
  label,
}: ExportColumnManifestButtonProps) {
  const { t } = useTranslation('exports');
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={className ?? 'h-9 cursor-pointer text-xs'}
      onClick={() => {
        downloadCsv(
          `${fileLabel}-columns.csv`,
          buildColumnManifest({ columns, selected, transformations }),
          [
            { key: 'key', label: 'Column' },
            { key: 'label', label: 'Label' },
            { key: 'kind', label: 'Kind' },
            { key: 'sourceColumns', label: 'Source column' },
            { key: 'identifier', label: 'Identifier' },
            { key: 'encoding', label: 'Encoding' },
          ],
        );
      }}
    >
      {label ? (
        <>
          {label}
          <Download aria-hidden="true" className="ml-1 size-3.5" />
        </>
      ) : (
        <>
          <Download aria-hidden="true" className="mr-1.5 size-3.5" />
          {t('estimate.columns.downloadList')}
        </>
      )}
    </Button>
  );
}
